import React, { useState } from 'react';
import { toast } from 'sonner';
import { Rocket, Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { videoApiClient } from '@/services/videoApi';
import { trackEvent } from '@/lib/analytics';

/**
 * "Launch on CTV" lead form.
 *
 * Collects follow-up details and sends a message to the CornerCast team (via
 * the back-end → Google Chat), mirroring the marketing-site contact form. It
 * does NOT launch a campaign. The generated creative's id/URL are attached
 * automatically so the team can reference it.
 */
const LaunchOnCtvForm = ({ open, onOpenChange, creativeId, creativeUrl, defaults = {} }) => {
  const [form, setForm] = useState({
    name: defaults.name || '',
    email: defaults.email || '',
    business_name: defaults.business_name || '',
    maps_or_website: defaults.maps_or_website || '',
    location: '',
    budget: '',
    timing: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error('Please enter an email so we can follow up.');
      return;
    }
    setSubmitting(true);
    try {
      await videoApiClient.submitCtvLead({
        ...form,
        creative_id: creativeId || null,
        creative_url: creativeUrl || null,
      });
      setSubmitted(true);
      trackEvent('ctv_lead_submitted', { creative_id: creativeId || null });
      toast.success("Thanks! We'll reach out about launching your creative.");
    } catch (err) {
      toast.error(err?.message || 'Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    if (!next) setSubmitted(false);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {submitted ? (
          <div className="flex flex-col items-center text-center py-8 space-y-3">
            <CheckCircle2 className="h-12 w-12 text-teal" />
            <DialogTitle>Request received</DialogTitle>
            <DialogDescription>
              Our team will reach out shortly about launching your creative on CTV.
            </DialogDescription>
            <Button className="mt-2" onClick={() => handleOpenChange(false)}>Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-coral" />
                Launch on CTV
              </DialogTitle>
              <DialogDescription>
                Tell us a bit more and the CornerCast team will help get this creative
                onto connected TV. This does not start a campaign automatically.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ctv-name">Name</Label>
                  <Input id="ctv-name" value={form.name} onChange={update('name')} placeholder="Jane Smith" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ctv-email">Email *</Label>
                  <Input id="ctv-email" type="email" required value={form.email} onChange={update('email')} placeholder="you@business.com" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ctv-business">Business name</Label>
                <Input id="ctv-business" value={form.business_name} onChange={update('business_name')} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ctv-maps">Google Maps link or website</Label>
                <Input id="ctv-maps" value={form.maps_or_website} onChange={update('maps_or_website')} placeholder="https://maps.google.com/…" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ctv-location">Campaign location</Label>
                  <Input id="ctv-location" value={form.location} onChange={update('location')} placeholder="Austin, TX" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ctv-budget">Approx. budget</Label>
                  <Input id="ctv-budget" value={form.budget} onChange={update('budget')} placeholder="$2,000 / mo" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ctv-timing">Desired timing</Label>
                <Input id="ctv-timing" value={form.timing} onChange={update('timing')} placeholder="Next month" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ctv-notes">Additional notes</Label>
                <Textarea id="ctv-notes" rows={3} value={form.notes} onChange={update('notes')} />
              </div>

              {creativeId && (
                <p className="text-xs text-muted-foreground">
                  Creative <span className="font-mono">{creativeId}</span> will be attached automatically.
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-coral hover:bg-coral-hover text-white border-none">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    'Send request'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LaunchOnCtvForm;
